import DocumentHeader from '@/components/DocumentHeader';
import { useSEO, useSummary } from '@/hooks/useCompany';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import type { CompanyReportSection } from '../../useSection';
import useShouldNoIndexFollow from '../../hooks/useShouldNoIndexFollow';

export function SEOHeader({ section }: { section: CompanyReportSection }) {
  const summary = useSummary();
  const seo = useSEO(section);
  const noIndexFollow = useShouldNoIndexFollow();
  const ogImage = useCloudflareImage({
    src: seo.status === 'success' ? seo.mainHeader : '',
    width: 1200,
    height: 627,
    fit: 'cover',
  });

  const coverImage = useCloudflareImage({
    src: seo.status === 'success' ? seo.mainHeader : '',
    width: 1167,
    height: 198,
    fit: 'cover',
  });
  const responsiveImages = getResponsiveImage({
    src: seo.status === 'success' ? seo.mainHeader : '',
    breakpoints: [
      { width: 1167, height: 198 },
      { width: 992, height: 198 },
      { width: 768, height: 228 },
      { width: 576, height: 276 },
    ],
  });
  const imgPreloadHack = {
    imagesrcset: responsiveImages.srcSet,
  };

  if (summary.status === 'loading' || seo.status === 'loading') return null;

  return (
    <DocumentHeader
      title={seo.title}
      allowCrawlers={!noIndexFollow && summary.allowCrawlers}
      allowFollow={noIndexFollow}
      description={seo.description}
    >
      <link rel="preload" as="image" href={coverImage} {...imgPreloadHack} />
      <link rel="image_src" href={ogImage} />
      <meta property="og:image" content={ogImage} />
      <link rel="canonical" href={seo.canonicalUrlWithHostAndSection} />
      {seo.alternates.map((alt) => (
        <link
          key={alt.lang}
          rel="alternate"
          hrefLang={alt.lang}
          href={alt.href}
        />
      ))}
      <meta property="article:tag" content={seo.exchangeTicker} />
      <meta
        name="ticker"
        itemProp="tickersymbol"
        content={seo.exchangeTicker}
      />
      <meta name="tickers" content={seo.exchangeTicker} />
      <meta name="keywords" content={seo.keywords} />
      <meta name="tags" content={seo.tags} />
    </DocumentHeader>
  );
}
